import React, { useCallback, useRef } from "react";
import { WEBGL } from "./utils/webGL.utils";
import Main3DManager from "./main.3d.manager";
import "./styles/art.space.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useArtSpace from "../../hooks/useArtSpace";
import useEvents from "../../hooks/useEvents";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import { ApiClient } from "../../utils/apiClient";
import User from "../../models/user.model";
import { Artwork, ArtworkWrapper } from "@/types/artworksData";
import AppData from "./app.data";
import { Slot } from "@/types/slotsData";
import { resolveActiveMoodIndex } from "../../utils/moodVisibility";
import {
  buildTelemetrySpaceContext,
  trackArtworkView,
  trackGalleryView,
  type TelemetrySpaceContext,
} from "../../utils/telemetry";
import { useGalleryChromeContext } from "../../contexts/GalleryChromeContext";
import { isGalleryBackgroundMusicPlayable } from "../../utils/visitorPreferences";
const appData = new AppData()

const BASE_API = process.env.REACT_APP_BASE_API;

const SpaceComponent = React.memo(() => {
  const { id } = useParams()
  const user: User = useSelector((state: AppState) => state.auth.user);
  appData.setUserData(user)
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const { listedVouchers } = useSelector((state: AppState) => state.nft);
  const galleryTelemetryContextRef = useRef<TelemetrySpaceContext>({});
  const loadedGalleryIdRef = useRef<string | null>(null);
  const trackedGalleryViewRef = useRef<string | null>(null);
  const { setHasPlayableGalleryBackgroundMusic } = useGalleryChromeContext();

  const [main3DManager, setMain3DManager] = React.useState<
    Main3DManager | undefined
  >();

  // const [spaceData, artworksData] = useArtSpace();

  const onDioramaButton = useCallback((slot: Slot) => {
    try {
      const selectedArtwork = slot.artworkWrapper.artworks[slot.artworkWrapper.activeArtwork]
      navigate(`/gallery/${selectedArtwork.galleryId}`)
    } catch (e) {
      console.log(e); // Logs the error
    }
  }, []);

  const onArtworkClicked = useCallback((artwork: ArtworkWrapper) => {
    const activeArtworkIndex = Number(artwork?.activeArtwork);
    const selectedArtwork = artwork?.artworks?.[Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0];
    if (selectedArtwork?._id && loadedGalleryIdRef.current) {
      trackArtworkView(
        String(selectedArtwork._id),
        `${location.pathname}${location.search || ""}`,
        loadedGalleryIdRef.current,
        undefined,
        galleryTelemetryContextRef.current
      );
    }
    console.log("Clicked on artwork with data: ", artwork)
  }, [location.pathname, location.search]);

  const onSpaceEditClicked = useCallback((data: any) => {
    console.log("Clicked on edit space: ", data)
  }, []);

  React.useEffect(() => {
    const manager = new Main3DManager();

    appData.setMain3DManager(manager);
    setMain3DManager(manager);

    return () => {
      manager.destroy();           // destroy once at unmount/user change
      appData.setMain3DManager(null);
      setMain3DManager(null);
    };
  }, []);

  React.useEffect(() => {
    if (!main3DManager || !canvasRef.current) return;

    main3DManager.init(canvasRef.current);
    main3DManager.setDioramaClickEvent(onDioramaButton);
    main3DManager.setArtworkClickEvent(onArtworkClicked);
    main3DManager.setSpaceEditClickEvent(onSpaceEditClicked);

  }, [main3DManager, onArtworkClicked, onDioramaButton, onSpaceEditClicked]);

  useEvents(main3DManager, canvasRef, listedVouchers);

  
  React.useEffect(() => {
    let cancelled = false;

    const apiClient = ApiClient(BASE_API);
    if (main3DManager && WEBGL.isWebGLAvailable()) {

      const init3D = async () => {
        try {
          setHasPlayableGalleryBackgroundMusic(false);
          let galleryResp;
          if (user) {
            galleryResp = await apiClient.getMoMGalleryByUserId();
          } else {
            galleryResp = await apiClient.getDefaultMoMGallery();
          }
          if (!galleryResp || cancelled) return;

          // load gallery data
          const { gallery } = galleryResp;
          const spaceData = await apiClient.getSpaceById(gallery.spaceId);
          if (cancelled) return;

          const fallbackMood =
            Number.isInteger(gallery?.data?.mood) ? gallery.data.mood : undefined;
          const resolvedMood = resolveActiveMoodIndex(
            spaceData?.space?.data?.moods,
            fallbackMood,
            {
              userId: user?._id,
              galleryId: gallery?._id
            }
          );
          if (!gallery.data) {
            gallery.data = {} as any;
          }
          gallery.data.mood = resolvedMood;
          setHasPlayableGalleryBackgroundMusic(
            isGalleryBackgroundMusicPlayable(gallery.data?.sound)
          );

          appData.setGalleryData(gallery);
          appData.setSpaceData(spaceData.space);
          const telemetryContext = buildTelemetrySpaceContext(gallery, spaceData.space);
          galleryTelemetryContextRef.current = telemetryContext;
          loadedGalleryIdRef.current = gallery?._id ? String(gallery._id) : null;
          const galleryTelemetryKey = `${loadedGalleryIdRef.current}:${location.pathname}${location.search || ""}`;
          if (loadedGalleryIdRef.current && trackedGalleryViewRef.current !== galleryTelemetryKey) {
            trackedGalleryViewRef.current = galleryTelemetryKey;
            trackGalleryView(
              loadedGalleryIdRef.current,
              `${location.pathname}${location.search || ""}`,
              undefined,
              telemetryContext
            );
          }

          appData.setUserData(user);

          await apiClient.getDioramaArtworks();
          if (cancelled) return;

          console.log("%cLoading data: ", "color:green", appData);
          await main3DManager.loadGallery();
        } catch (e) {
          console.error("init3D error:", e);
        }
      };
      init3D();
    }

    return () => {
      cancelled = true; // prevent late calls after unmount
      setHasPlayableGalleryBackgroundMusic(false);
    };
  }, [location.pathname, location.search, main3DManager, setHasPlayableGalleryBackgroundMusic, user]);

  return (
    <>
      <div id="WebGL" draggable="false" ref={canvasRef}></div>
      {/* <div id="prompt-text"></div> */}
      <div id="loading" className="loading-screen">Loading ...</div>
      <div id="joystick-background"></div>
      <div id="joystick-thumb"></div>
    </>
  );
});

export { SpaceComponent };
